import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Card,
  Row,
  Col,
  Tag,
  Space,
  InputNumber,
  Image,
} from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductById } from '../store/slices/productsSlice';
import { addToCart } from '../store/slices/cartSlice';
import { formatPrice } from '../utils/formatters';
import Loader from '../components/common/Loader';
import useTelegram from '../hooks/useTelegram';

const { Title, Text, Paragraph } = Typography;

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedProduct: product, loading } = useSelector((state) => state.products);
  const [quantity, setQuantity] = useState(1);
  const { showBackButton, hideBackButton, hapticFeedback } = useTelegram();

  useEffect(() => {
    dispatch(fetchProductById(id));
  }, [dispatch, id]);

  useEffect(() => {
    // Show Telegram back button
    showBackButton(() => navigate(-1));
    return () => hideBackButton();
  }, [showBackButton, hideBackButton, navigate]);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      dispatch(addToCart(product));
    }
    hapticFeedback('notification', 'success');
    navigate('/cart');
  };

  const handleQuantityChange = (val) => {
    if (val >= 1 && val <= product.stock_quantity) {
      setQuantity(val);
      hapticFeedback('selection');
    }
  };

  if (loading || !product) {
    return <Loader text="Mahsulot yuklanmoqda..." />;
  }

  const price = parseFloat(product.discount_price || product.price);
  const originalPrice = product.discount_price ? parseFloat(product.price) : null;
  const discount = originalPrice
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : null;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 0' }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16, color: '#f8fafc', fontWeight: 500 }}
      >
        Orqaga
      </Button>

      <Row gutter={[32, 32]}>
        <Col xs={24} md={12}>
          <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#fff', padding: 24, borderRadius: 8 }}>
              <Image
                src={
                  product.image_urls && product.image_urls.length > 0
                    ? `${process.env.REACT_APP_API_BASE_URL}${product.image_urls[0]}`
                    : 'https://via.placeholder.com/400x400?text=No+Image'
                }
                alt={product.name}
                style={{ maxHeight: 400, objectFit: 'contain', width: '100%' }}
                fallback="https://via.placeholder.com/400x400?text=Error"
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card bordered={false} style={{ borderRadius: 24, boxShadow: '0 12px 32px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <Title level={2} style={{ marginBottom: 8, color: '#0f172a' }}>
                  {product.name}
                </Title>
                {product.category && (
                  <Tag color="blue">{product.category.name}</Tag>
                )}
              </div>

              <div>
                {discount && (
                  <Tag color="error" style={{ fontSize: 14, padding: '4px 8px' }}>
                    -{discount}%
                  </Tag>
                )}
                {originalPrice && (
                  <Text delete type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    {formatPrice(originalPrice)}
                  </Text>
                )}
                <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                  {formatPrice(price)}
                </Title>
              </div>

              <Paragraph type="secondary" style={{ color: '#475569', fontSize: 16 }}>
                {product.description}
              </Paragraph>

              <div>
                <Text type="secondary" style={{ color: '#64748b' }}>
                  Omborda: {product.stock_quantity} dona
                </Text>
              </div>

              {product.is_available && product.stock_quantity > 0 ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Text strong style={{ color: '#0f172a' }}>Miqdor:</Text>
                    <Space>
                      <Button
                        icon={<MinusOutlined />}
                        onClick={() => handleQuantityChange(quantity - 1)}
                        disabled={quantity <= 1}
                      />
                      <InputNumber
                        min={1}
                        max={product.stock_quantity}
                        value={quantity}
                        onChange={handleQuantityChange}
                        controls={false}
                        style={{ width: 60, textAlign: 'center' }}
                      />
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => handleQuantityChange(quantity + 1)}
                        disabled={quantity >= product.stock_quantity}
                      />
                    </Space>
                  </div>

                  <div>
                    <Text type="secondary">
                      Jami: {formatPrice(price * quantity)}
                    </Text>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    shape="round"
                    icon={<ShoppingCartOutlined />}
                    onClick={handleAddToCart}
                    block
                    style={{ height: 48, marginTop: 16 }}
                  >
                    Savatga qo&apos;shish
                  </Button>
                </>
              ) : (
                <Button type="default" size="large" shape="round" block disabled style={{ height: 48, marginTop: 16 }}>
                  Mavjud emas
                </Button>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProductDetailsPage;
